import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import qs from 'qs';
import { DateRange } from '@blueprintjs/datetime';
import {
  VColorPicker,
  VInputFieldWithSuggestions,
  VNumericField,
  VSearchSelectField,
  VSelectMultipleTags
} from 'welltrax-ui-components';
import { Dictionary, find, forEach, get, groupBy, isArray, isNil, orderBy, replace, uniqBy, upperCase } from 'lodash';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { useMeasure } from 'react-use';

import { StoreAppContext } from '../../../../../../App';
import {
  getAccountNumber,
  getCommodityArrayParameter,
  getOperatorNumber,
  getPickUpContactInfo,
  getPickUpsByIdFromAccount
} from '../../../../../../modules/commons/utils/utils';
import { getDocuments } from '../../../../../hooks/local_db/functions';
import { ISelectItem } from '../../../../../types/ItemSelectType';
import { IFilterType, ISourceDescriptionOptions, ISourceQueryParams } from '../../../../../types/ProfileTypes';
import { intl } from '../../../../../utils/Intl';
import { buildShortcuts } from '../../../../../utils/utils';
import { ToggleUpDown } from '../../../ToggleUpDown/ToggleUpDown';
import { ProfileUtils } from '../../utils/ProfileUtils';
import { CustomTimeSelectBody } from '../styles';
import { AdvanceSection } from './AdvanceSection/AdvanceSection';
import { CustomDateRange } from './CustomDateRange/CustomDateRange';
import { SwitchCustomTimeSelect } from './CustomTimeSelect/SwitchCustomTimeSelect';
import { GroupButtons } from './GroupButtons/GroupButtons';
import {
  StyledDateRangeField,
  StyledInputField,
  StyledVTagInputField,
  TagInputContainerWithTooltipStyled
} from './styled';
import { companyInfo } from '../../../../../utils/utilCompany';
import genericService from '../../../../../../core/services/GenericService';
import IndeterminateActionButton from '../../../../../../modules/commons/components/IndeterminateActionButton/IndeterminateActionButton';
import SearchSelectLargeData from '../../../../../../modules/commons/components/Form/SearchSelectLargeData/SearchSelectLargeData';
import DateTimeFilter from './DateFilter/DateTimeFilter';
import appStore from '../../../../../stores/AppStore';
import { RadioYesNo } from '../../../../../../modules/dispatch/components/dispatchLoad/components/navBar/RadioYesNo';

interface IProps {
  filter: IFilterType;
  disabled?: boolean;
  onChange: (value: any) => void;
  showLabel?: boolean;
  type?: 'filter' | 'conditional';
  onTable?: boolean;
  fixedInputWidthPx?: number;
  tipLabel?: string;
  ref?: any;
  isStacked?: boolean;
  minimal?: boolean;
  isQueryFilterInForm?: boolean;
  advance?: boolean;
  getValueOnSelectMenuItem?: boolean;
}

const omitFiltersIfLargeDataSet = ['pickupId', 'dropOffId', 'pickUp1', 'pickUp2', 'dropOff1', 'dropOff2'];

const omitArrayFiltersIfLargeDataSet = ['pickUpIdArr', 'dropOffIdArr'];

export interface ISelectItemStatus extends ISelectItem {
  status: boolean;
  terminalId?: string | number;
}

const FilterField = observer((props: IProps) => {
  const [options, setOptions] = useState<ISelectItem[]>([]);
  const [filter, setFilter] = useState<IFilterType>(props.filter);
  const [allowFirstLoad, setAllowFirstLoad] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disableContext, setDisableContext] = useState<boolean>(false);
  const refReplaceSource = useRef<Record<string, unknown>>({});
  const appContext = useContext(StoreAppContext);
  const [refInputComponent, { width: widthComponent }] = useMeasure();
  const [valueMultiSelectTags, setValueMultiSelectTags] = useState([]);
  const { minimal } = props;

  useEffect(() => {
    if (ProfileUtils.fieldsWithOptions.includes(filter.type || '')) {
      getOptions();
    }
  }, [filter?.source, filter?.additional_value]);

  useEffect(() => {
    filter.type === 'MULTI_SELECT_TAGS' && setValueMultiSelectTags(filter.value);
    if (
      filter.type === 'MULTI_SELECT' &&
      (filter.field === 'pickUpIdArr' || filter.field === 'dropOffIdArr') &&
      companyInfo.largeDatasetEnabled
    ) {
      if (!filter.value.length) {
        setAllowFirstLoad(true);
        onChange('');
      }
    }
  }, [filter.value, options]);

  useEffect(() => {
    if (filter.type === 'SELECT' || (filter.type === 'MULTI_SELECT' && filter.typeSource === 'URL')) {
      hasChangeExtractAndReplaceFilter() && getOptions();
    }
    if (
      (((filter.replaceSource && filter.replaceSource.length > 0) ?? false) ||
        filter.typeSource === 'ARRAY_CONTEXT' ||
        filter?.replaceSourceQueryParams?.from ||
        filter.typeSource === 'ARRAY_CONTEXT_OBJECT') &&
      (filter.type === 'SELECT' || filter.type === 'MULTI_SELECT')
    ) {
      hasChangeExtractAndReplaceFilter() && getOptions();
    }

    if (filter.disabledContext) {
      const disabled = !(
        appContext.app.filterContext?.hasOwnProperty(filter.disabledContext) &&
        appContext.app.filterContext[filter.disabledContext]
      );
      setDisableContext(disabled);
    }
  }, [appContext.app.filterContext]);

  useEffect(() => {
    if (
      omitFiltersIfLargeDataSet.filter(item => item !== 'dropOffId').includes(filter.field) &&
      !!companyInfo.largeDatasetEnabled
    ) {
      setAllowFirstLoad(true);
      onChange('');
    }
  }, [appContext.app.filterContext.pickUpAccountId]);

  useEffect(() => {
    if (filter.field === 'dropOffId' && !!companyInfo.largeDatasetEnabled) {
      setAllowFirstLoad(true);
      onChange('');
    }
  }, [appContext.app.filterContext.dropOffAccount]);

  const hasChangeExtractAndReplaceFilter = (): boolean => {
    if (filter.typeSource === 'URL') {
      const sources: Record<string, unknown> = {};
      if (filter.replaceSource) {
        filter.replaceSource.forEach((value: string) => {
          sources[value] = toJS(get(appContext.app.filterContext, value));
        });
      } else if (filter?.replaceSourceQueryParams?.from) {
        sources[filter.replaceSourceQueryParams.from] = toJS(
          get(appContext.app.filterContext, filter.replaceSourceQueryParams.from)
        );
      }

      if (filter?.conditionalInContext) {
        sources[filter.conditionalInContext] = toJS(get(appContext.app.filterContext, filter.conditionalInContext));
      }

      if (JSON.stringify(sources) === JSON.stringify(refReplaceSource.current)) {
        return false;
      }
      refReplaceSource.current = sources;
    }
    return true;
  };

  useEffect(() => {
    setFilter(props.filter);
  }, [props.filter]);

  useEffect(() => {
    cleanWrongFilterOptionValue();
  }, [options]);

  const cleanWrongFilterOptionValue = () => {
    if (filter.type !== 'AUTOCOMPLETE' && filter.type !== 'CUSTOM_TIME_SELECT') {
      if (options.length > 0 && filter.value !== '') {
        const item = find(options, (field: any) => {
          if (filter.type === 'SELECT' || filter.type === 'GROUP_BUTTONS') {
            return filter.value === field.value;
          }
          if (filter.type === 'MULTI_SELECT' || filter.type === 'MULTI_SELECT_TAGS') {
            return filter.value.includes(field.value);
          }
        });
        if (!item) {
          onChange('');
        }
      }
    }
  };

  const getOptions = useCallback(() => {
    if (
      (companyInfo.largeDatasetEnabled &&
        !omitFiltersIfLargeDataSet.includes(filter.field) &&
        !omitArrayFiltersIfLargeDataSet.includes(filter.field)) ||
      !companyInfo.largeDatasetEnabled
    ) {
      const typeSource = filter.typeSource;
      switch (typeSource) {
        case 'ARRAY':
          let options = filter.source;
          if (filter.additional_value && isArray(filter.additional_value)) {
            options = [...options, ...filter.additional_value];
          }
          setOptions(options);
          break;
        case 'ARRAY_CONTEXT':
          if (filter.source && appContext.app.filterContext.hasOwnProperty(filter.source)) {
            setOptions(appContext.app.filterContext[filter.source]);
            break;
          }
          setOptions([]);
          break;
        case 'ARRAY_CONTEXT_OBJECT':
          if (filter.source) {
            const obj = filter.source.split('/');
            if (obj.length > 0 && appContext.app.filterContext.hasOwnProperty(obj[0])) {
              setOptions(parseOptionsArrayContextObject(obj));
            }
            break;
          }
          setOptions([]);
          break;
        case 'URL':
          getAPIOptions(filter);
          break;
      }
    }
  }, [filter]);

  const getAPIOptions = (filter: IFilterType) => {
    let url = filter.source;
    if (filter.urlType && filter.field) {
      switch (filter.urlType) {
        case 'CONCAT':
          if (filter?.replaceSourceQueryParams) {
            url = getUrlConcatQueryParams(url, filter?.replaceSourceQueryParams);
          } else {
            url = getUrlConcat(url, filter.field);
          }
          break;
        case 'REPLACE':
          url = getUrlReplace(url, filter.replaceSource || []);
          break;
        case 'MULTIPLE_REPLACE':
          getUrlMultipleReplace(url, filter.replaceSource || []);
          return;
      }
    }
    setIsLoading(true);
    switch (filter.field) {
      case 'commodityId':
      case 'county':
      case 'state':
      case 'pickupCounty':
      case 'pickupState':
      case 'dropOffCounty':
      case 'dropOffState':
        getDocuments(url, filter.field === 'commodityId' ? 'Commodity' : 'CountyState', (value: any) => {
          setOptions(parseOptions(value, filter));
          setIsLoading(false);
        });
        break;
      default:
        genericService
          .getAll(url, {})
          .then((response: any) => {
            if (response && response.data) {
              if (filter.hasOwnProperty('sourceToSaveInContext')) {
                appContext.app.setFilterContext({
                  ...appContext.app.filterContext,
                  [`${filter.sourceToSaveInContext}`]: response.data
                });
              }
              if (filter.field === 'subTerminal') {
                const terminalIds: any[] = appContext.app.filterContext?.terminalIds ?? [];
                const subTerminals: any[] = parseOptions(response.data, filter);
                setOptions(subTerminals?.filter(subterminal => terminalIds?.includes(subterminal.terminalId)));
              } else {
                setOptions(parseOptions(response.data, filter));
              }
            }
            setIsLoading(false);
          })
          .catch(() => {
            setIsLoading(false);
          });
    }
  };

  const getExtraParams = (isPickUp = false) => {
    const account = isPickUp
      ? appContext.app.filterContext.pickUpAccountId
      : appContext.app.filterContext.dropOffAccount;
    const accountId = !!account ? `&paramColumnValue=${account}` : '';

    const commodityIds = appContext.app.filterContext?.hasOwnProperty('commodityIdArr')
      ? appContext.app.filterContext.commodityIdArr
      : [appStore.selectedCommodity];

    return { accountId, commodityIds };
  };

  const getAPIOptionsAsyncSelect = async (filter: IFilterType, search?: string, isPickUp = false) => {
    let url = filter.source;
    const nameSearch = search ? `&nameSearch=${encodeURIComponent(search)}` : '';
    const { accountId, commodityIds } = getExtraParams(isPickUp);
    const result: any = [];

    if (filter.urlType && filter.field) {
      switch (filter.urlType) {
        case 'CONCAT':
          if (filter?.replaceSourceQueryParams) {
            url = getUrlConcatQueryParams(url, filter?.replaceSourceQueryParams);
          } else {
            url = getUrlConcat(url, filter.field);
          }
          break;
        case 'REPLACE':
          url = getUrlReplace(url, filter.replaceSource || []);
          break;
        case 'MULTIPLE_REPLACE':
          getUrlMultipleReplace(url, filter.replaceSource || []);
          return;
      }
    }

    if (search) {
      url = `${url}${nameSearch}`;
    }

    if (!!accountId) {
      url = `${url}${accountId}`;
    }

    if (filter.type == 'MULTI_SELECT') {
      url = `${url}${commodityIds ? getCommodityArrayParameter(commodityIds) : ''}`;
    }

    setIsLoading(true);
    switch (filter.field) {
      case 'commodityId':
      case 'county':
      case 'state':
      case 'pickupCounty':
      case 'pickupState':
      case 'dropOffCounty':
      case 'dropOffState':
        getDocuments(url, filter.field === 'commodityId' ? 'Commodity' : 'CountyState', (value: any) => {
          setIsLoading(false);
          return parseOptions(value, filter);
        });
        break;
      default:
        const response = await genericService.getAll(url, {});
        if (response && response.data) {
          if (filter.hasOwnProperty('sourceToSaveInContext')) {
            appContext.app.setFilterContext({
              ...appContext.app.filterContext,
              [`${filter.sourceToSaveInContext}`]: response.data
            });
          }
          setAllowFirstLoad(false);
          return parseOptions(response.data, filter);
        }
        setAllowFirstLoad(false);
        setIsLoading(false);
    }
    return result;
  };

  const getUrlConcat = (source: string, field: string) => {
    let url = source;

    if (url && field && appContext.app.filterContext?.field) {
      const urlParams = qs.stringify(appContext.app.filterContext?.field, { arrayFormat: 'repeat' });
      url += `?${urlParams}`;
    }
    return url;
  };

  const getUrlReplace = (source: string, params: string[]) => {
    let url = source;
    if (url && params.length > 0) {
      params.forEach(value => {
        if (appContext.app.filterContext?.hasOwnProperty(value)) {
          url = url.replace(`{${value}}`, appContext.app.filterContext[value]);
        }
      });
    }
    return url;
  };

  const getUrlConcatQueryParams = (source: string, params?: ISourceQueryParams) => {
    let url = source;
    if (url && params) {
      if (
        appContext.app.filterContext?.hasOwnProperty(params.from) &&
        isArray(appContext.app.filterContext[params.from])
      ) {
        const urlParams = qs.stringify(appContext.app.filterContext[params.from], {
          arrayFormat: 'repeat',
          encoder: (str: string, defaultEncoder: any, charset: string, type: 'key' | 'value'): string => {
            const strNew = type === 'key' ? (params.to ? params.to : params.from) : str;
            return defaultEncoder(strNew, defaultEncoder, charset);
          }
        });
        url += `?${urlParams}`;
      }
    }
    return url;
  };

  const getUrlMultipleReplace = (source: string, params: string[]) => {
    const urls: any[] = [];
    if (source && params.length > 0) {
      params.forEach(value => {
        if (appContext.app.filterContext?.hasOwnProperty(value)) {
          const contextOptions = appContext.app.filterContext[value];
          if (isArray(contextOptions)) {
            contextOptions.forEach((item: any) => {
              urls.push(source.replace(`{${value}}`, item));
            });
          }
        }
      });
    }
    const promises = urls.map(url => genericService.getAll(url, {}));
    Promise.all(promises).then(response => {
      if (filter.hasOwnProperty('sourceToSaveInContext')) {
        appContext.app.setFilterContext({
          ...appContext.app.filterContext,
          [`${filter.sourceToSaveInContext}`]: response.map((item: any) => item.data)
        });
      }
      const data = response.map((item: any) => parseOptions(item.data, filter));
      setOptions(
        data.length > 0
          ? data.reduce((previousValue: ISelectItem[], currentValue: ISelectItem[]) => [
              ...previousValue,
              ...currentValue
            ])
          : []
      );
    });
  };

  const parseOptions = (data: any[], filter: IFilterType) => {
    let items: ISelectItemStatus[] = [];
    data.forEach((value: any) => {
      let label = '';
      let val = '';
      let status = true;
      let terminalId = '';
      switch (filter.field) {
        case 'county':
        case 'pickupCounty':
        case 'dropOffCounty':
          label = value.county;
          val = value.county;
          break;
        case 'state':
        case 'pickupState':
        case 'dropOffState':
          label = value.state;
          val = value.state;
          break;
        case 'accountDistrictIdList':
          label = value?.contact?.fullName;
          val = value.id;
          break;
        case 'supervisorIdArr':
        case 'supervisor':
          label = value.description;
          val = value.id;
          break;
        case 'rtuStatus':
          label = replace(value, new RegExp('_', 'g'), ' ');
          val = value;
          break;
        case 'companyName':
          label = value;
          val = value;
          break;
        case 'pickup':
          label = `${value.name || value.fullName || value.description || value.label} (${
            value?.extraColumns?.PU_ACCOUNT_NAME
          }) ${value?.extraColumns?.PU_OPERATOR_NAME ? `(${value?.extraColumns?.PU_OPERATOR_NAME})` : ''}`;
          val = filter.source_value ? value[filter.source_value] || label : value.id || value.value;
          break;
        case 'dropOff':
          label = `${value.name || value.fullName || value.description || value.label} (${
            value?.extraColumns?.DO_ACCOUNT_NAME
          }) ${value?.extraColumns?.DO_OPERATOR_NAME ? `(${value?.extraColumns?.DO_OPERATOR_NAME})` : ''}`;
          val = filter.source_value ? value[filter.source_value] || label : value.id || value.value;
          break;
        case 'accountIdArr':
          label = `${value.name || value.fullName || value.description || value.label} ${getAccountNumber(value)}`;
          val = filter.source_value ? value[filter.source_value] || label : value.id || value.value;
          break;
        case 'operatorIdArr':
          label = `${value.name || value.fullName || value.description || value.label} ${getOperatorNumber(value)}`;
          val = filter.source_value ? value[filter.source_value] || label : value.id || value.value;
          break;
        case 'subTerminal':
          label = value.name || value.fullName || value.description || value.label || value.contact.fullName;
          val = filter.source_value ? value[filter.source_value] || label : value.id || value.value;
          terminalId = value?.terminalId;
          break;
        default:
          label = value.name || value.fullName || value.description || value.label || value.contact.fullName;
          val = filter.source_value ? value[filter.source_value] || label : value.id || value.value;
      }

      const additionalSourceDescriptionInOptions: ISourceDescriptionOptions[] =
        filter?.additionalSourceDescriptionInOptions ?? [];
      if (
        additionalSourceDescriptionInOptions &&
        isArray(additionalSourceDescriptionInOptions) &&
        additionalSourceDescriptionInOptions.length > 0
      ) {
        const converter = additionalSourceDescriptionInOptions
          .map((item: ISourceDescriptionOptions) => {
            const label = get(value, item.source);
            return !!label ? `${item?.startPath ?? ''}${label.trim()}${item?.endPath ?? ''}`.trim() : '';
          })
          .filter((item: string) => !!item)
          .join(' ');

        !!converter && (label = `${label} ${converter}`);
      }

      if (filter?.keyStatusLabel && value.hasOwnProperty(filter.keyStatusLabel)) {
        const statusItem = value[filter.keyStatusLabel];
        label = `${label} ${ProfileUtils.getInactiveStatus(statusItem)}`;
        status = statusItem;
      }

      items.push(
        filter.field === 'subTerminal'
          ? {
              label,
              status,
              value: val,
              terminalId
            }
          : {
              label,
              status,
              value: val
            }
      );
    });

    if (filter.additional_value && isArray(filter.additional_value)) {
      items = [...items, ...filter.additional_value];
    }

    const mapToResponse = (itemsResponse: ISelectItemStatus[]) => {
      return itemsResponse.map((it: ISelectItemStatus) => ({ label: it.label, value: it.value }));
    };

    const mapSubTerminalToResponse = (itemsResponse: ISelectItemStatus[]) => {
      return itemsResponse.map((it: ISelectItemStatus) => ({
        label: it.label,
        value: it.value,
        terminalId: it.terminalId
      }));
    };

    const orderItems = (itemsGroups: Dictionary<ISelectItemStatus[]>, key: string) => {
      if (!!itemsGroups[key]?.length) {
        return orderBy(mapToResponse(itemsGroups[key]), 'label');
      }
      return [];
    };

    if (filter?.keyStatusLabel && filter?.disabledSort) {
      const groupItems = groupBy(items, 'status');
      const active = orderItems(groupItems, 'true');
      const inactive = orderItems(groupItems, 'false');
      return [...active, ...inactive];
    }

    const list = orderBy(filter.field === 'subTerminal' ? mapSubTerminalToResponse(items) : mapToResponse(items), [
      'label'
    ]);
    return uniqBy(list, 'label');
  };

  const parseOptionsArrayContextObject = (obj: string[]) => {
    if (obj.length > 0) {
      const elements = appContext.app.filterContext[obj[0]];
      const mapper: ISelectItem[] = [];
      if (isArray(elements)) {
        forEach(elements, (item: any) => {
          let concat = item;
          for (const o of obj.slice(1)) {
            if (concat && concat.hasOwnProperty(o)) {
              concat = concat[o];
            } else {
              concat = '';
              break;
            }
          }

          const findElement = find(mapper, (itemMapper: ISelectItem) => itemMapper.value === concat);
          if (concat && concat !== '' && !findElement) {
            mapper.push({
              value: concat,
              label: upperCase(concat)
            });
          }
        });
      }
      return mapper;
    }
    return [];
  };

  const getFields = () => {
    const { disabled } = props;
    switch (filter.type) {
      case 'INPUT':
        return getVInputField(filter, onChange, !!disabled);

      case 'BOOLEAN':
        if (filter.field === 'includeHistoricalLoads') {
          return getRadioField(filter, onChange, disabled);
        } else {
          return getVCheckboxField(filter, onChange, disabled);
        }
      case 'NUMBER':
        return getVNumberField(filter, onChange, disabled);

      case 'PERCENT':
        return getVPNumberField(filter, onChange, !!disabled, { min: 0, max: 100 });

      case 'SELECT':
        return getVSelectField(filter, onChange, !!disabled);

      case 'MULTI_SELECT':
        return getVMultipleSelectField(filter, onChange, !!disabled);

      case 'TEXTAREA':
        return getVInputField(filter, onChange, !!disabled);

      case 'DATETIME':
      case 'DATE':
      case 'TIME':
        return (
          <DateTimeFilter
            {...{
              filter,
              onChange,
              disabled: !!disabled,
              type: filter.type,
              showLabel: props?.showLabel,
              disableContext: disableContext,
              tipLabel: props?.tipLabel,
              minimal: props?.minimal
            }}
          />
        );

      case 'DATERANGE':
      case 'CUSTOM_DATE_RANGE':
        return getVDateRangeField(filter, onChange, !!disabled);

      case 'COLOR':
        return getVColorField(filter, onChange, !!disabled);

      case 'INPUT_TAG':
        return getVTagsField(filter, onChange, !!disabled);

      case 'MULTI_SELECT_TAGS':
        return getVTagsMultipleField(filter, onChange, !!disabled);

      case 'ADVANCE_SECTION':
      case 'ADVANCE_HIDE_SECTION':
        return getAdvanceSection(filter, onChange, !!disabled);

      case 'GROUP_BUTTONS':
        return getGroupButtons(filter, onChange, !!disabled);
      case 'AUTOCOMPLETE':
        return getAutoComplete(filter, onChange, !!disabled);
      case 'CUSTOM_TIME_SELECT':
        return getCustomTimeSelect(filter, onChange, !!disabled);
      default:
        return getVInputField(filter, onChange, !!disabled);
    }
  };

  const onChange = (value: string) => {
    if (props.onChange) {
      const cf = {
        ...filter,
        value
      };
      setFilter(cf);
      props.onChange(cf);
      saveInContext(value);
    }
  };

  const saveInContext = (value: any) => {
    if (filter?.saveInContext) {
      appContext.app.setFilterContext({
        ...appContext.app.filterContext,
        [`${filter.field}`]: value
      });
    }
  };

  const onLoadData = async ({ search, firstLoad }: any) => {
    const isPickUp = filter.source.includes('PICK_UP');

    if (!search) {
      return [];
    }

    if (firstLoad) {
      const response = await getPickUpsByIdFromAccount(search);
      const options = response?.map((basic: any) => ({
        id: basic.id,
        value: basic.id,
        label: getPickUpContactInfo(basic),
        status: basic.status
      }));
      setAllowFirstLoad(false);
      return options;
    }
    return await getAPIOptionsAsyncSelect(filter, search, isPickUp);
  };

  const getCommonProps = () => {
    return {
      id: `filter${filter.field}`,
      tipLabel: ProfileUtils.getConditionalLabel(filter, appContext, !!props.tipLabel) ?? props.tipLabel,
      label: props.showLabel ? ProfileUtils.getConditionalLabel(filter, appContext, true) ?? filter.label : '',
      placeholder: `${intl('app.noSelection')}`,
      fixedInputWidthPx: props.fixedInputWidthPx ? props.fixedInputWidthPx : undefined,
      margin: '0',
      allowEmpty: !filter.notAllowEmptyItem,
      noLabel: !props.showLabel,
      isLoading: !companyInfo.largeDatasetEnabled ? isLoading : false,
      value: toJS(filter.value),
      options: options
    };
  };

  const getVSelectField = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    const { displayAsTree, treeChildIndentWidth } = filter;
    return omitFiltersIfLargeDataSet.includes(filter.field) && companyInfo.largeDatasetEnabled ? (
      <SearchSelectLargeData
        {...getCommonProps()}
        isLargeDataSetEnabled={companyInfo.largeDatasetEnabled || false}
        disabled={disabled}
        popoverWidth={widthComponent ?? undefined}
        amountCharacterSearch={companyInfo.minCharsForSearch}
        onLoadData={onLoadData}
        onChange={(value: any) => {
          if (onChange) {
            onChange(value);
          }
        }}
        inline
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        allowFirstLoad={allowFirstLoad}
      />
    ) : (
      <VSearchSelectField
        {...{ displayAsTree, treeChildIndentWidth }}
        {...getCommonProps()}
        sort={!filter?.disabledSort && filter.field !== 'shift' && filter.field !== 'workShift' ? 'asc' : undefined}
        inline
        fill
        disabled={disabled}
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        popoverMinimal
        onChange={(value: any) => {
          if (onChange) {
            onChange(value);
          }
        }}
        minimal={props.minimal}
      />
    );
  };

  const getAutoComplete = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    return (
      <VInputFieldWithSuggestions
        ref={props.ref}
        tipLabel={props.tipLabel}
        inline
        noLabel={!props.showLabel}
        fill
        margin={'0'}
        disabled={disabled}
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        value={toJS(filter.value)}
        label={props.showLabel ? filter.label : ''}
        id={'filter'}
        onChange={(value: any) => {
          if (onChange) {
            onChange(value);
          }
        }}
        minimal={props.minimal}
        options={options}
      />
    );
  };

  const getVMultipleSelectField = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    const conditionalForComponentValue = companyInfo.largeDatasetEnabled
      ? filter.value ?? []
      : options.length > 0
      ? filter.value || []
      : [];
    return (
      <div ref={refInputComponent}>
        {omitArrayFiltersIfLargeDataSet.includes(filter.field) && companyInfo.largeDatasetEnabled ? (
          <SearchSelectLargeData
            sort={!filter?.disabledSort ? 'asc' : undefined}
            tipLabel={props.tipLabel}
            allowEmpty
            inline
            multi
            margin={'0'}
            noLabel={!props.showLabel}
            disabled={disabled}
            layer={{
              inputWidth: props.showLabel ? 8 : 12,
              labelOrientation: 'start',
              labelWidth: props.showLabel ? 4 : undefined
            }}
            fixedInputWidthPx={props.fixedInputWidthPx ? props.fixedInputWidthPx : undefined}
            value={conditionalForComponentValue}
            options={options}
            label={props.showLabel ? filter.label : ''}
            id={`filter${filter.field}`}
            onChange={onChange}
            isLoading={!companyInfo.largeDatasetEnabled ? isLoading : false}
            minimal={props.minimal}
            popoverWidth={!isNil(widthComponent) ? widthComponent : undefined}
            isLargeDataSetEnabled={companyInfo.largeDatasetEnabled || false}
            amountCharacterSearch={companyInfo.minCharsForSearch}
            onLoadData={onLoadData}
            allowFirstLoad={allowFirstLoad}
          />
        ) : (
          <VSearchSelectField
            sort={!filter?.disabledSort ? 'asc' : undefined}
            tipLabel={props.tipLabel}
            allowEmpty
            inline
            multi
            margin={'0'}
            noLabel={!props.showLabel}
            fill
            popoverMinimal
            disabled={disabled}
            layer={{
              inputWidth: props.showLabel ? 8 : 12,
              labelOrientation: 'start',
              labelWidth: props.showLabel ? 4 : undefined
            }}
            fixedInputWidthPx={props.fixedInputWidthPx ? props.fixedInputWidthPx : undefined}
            value={options.length > 0 ? filter.value || [] : []}
            options={options}
            label={props.showLabel ? filter.label : ''}
            id={`filter${filter.field}`}
            onChange={onChange}
            isLoading={isLoading}
            minimal={props.minimal}
            getValueOnSelectMenuItem={props?.getValueOnSelectMenuItem}
            popoverWidth={!isNil(widthComponent) ? widthComponent : undefined}
          />
        )}
      </div>
    );
  };

  const getVInputField = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    return (
      <StyledInputField
        isStacked={!!props.isStacked}
        ref={props.ref}
        tipLabel={props.tipLabel}
        inline
        noLabel={!props.showLabel}
        fill
        margin={'0'}
        disabled={disabled}
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        value={toJS(filter.value)}
        label={props.showLabel ? filter.label : ''}
        id={'filter'}
        onChange={onChange}
        minimal={props.minimal}
      />
    );
  };

  const getVTagsField = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    if (props.onTable) {
      return getVMultipleSelectFieldForTags(filter, onChange, disabled);
    }

    return (
      <TagInputContainerWithTooltipStyled>
        <StyledVTagInputField
          {...{ minimal, onChange, disabled }}
          isStacked={!!props.isStacked}
          id={filter.field}
          ref={props.ref}
          inline
          noLabel={!props.showLabel}
          fill
          margin={'0'}
          label={props.showLabel ? filter.label : ''}
          layer={{
            inputWidth: props.showLabel ? 8 : 12,
            labelOrientation: 'start',
            labelWidth: props.showLabel ? 4 : undefined
          }}
          value={filter.value}
          tagValidation={filter.tagValidation}
        />
      </TagInputContainerWithTooltipStyled>
    );
  };

  const getVMultipleSelectFieldForTags = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    const optionsFilter: any[] =
      filter?.value && isArray(filter?.value) ? filter?.value.map((it: any) => ({ value: it, label: it })) : [];

    const onAddNewItem = (item: string) => {
      optionsFilter.push({ value: item, label: item });
      filter.value = filter.value && isArray(filter.value) ? [...filter.value, item] : [item];
    };

    const onChangeValue = (items: string[]) => {
      onChange(items);
    };

    return (
      <VSearchSelectField
        sort={'asc'}
        tipLabel={props.tipLabel}
        allowEmpty
        inline
        multi
        margin={'0'}
        noLabel={!props.showLabel}
        allowNewItem
        fill
        popoverMinimal
        disabled={disabled}
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        fixedInputWidthPx={props.fixedInputWidthPx ? props.fixedInputWidthPx : undefined}
        value={optionsFilter.length > 0 ? filter.value || [] : []}
        options={optionsFilter}
        label={props.showLabel ? filter.label : ''}
        id={`filter${filter.field}`}
        isLoading={isLoading}
        minimal={props.minimal}
        onAddNewItem={onAddNewItem}
        onChange={onChangeValue}
      />
    );
  };

  const getVTagsMultipleField = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    if (props.onTable) {
      return getVMultipleSelectField(filter, onChange, disabled);
    }

    return (
      <VSelectMultipleTags
        id={filter.field}
        inline
        noLabel={!props.showLabel}
        fill
        margin={'0'}
        disabled={disabled}
        label={props.showLabel ? filter.label : ''}
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        options={options}
        value={valueMultiSelectTags}
        onChange={onChange}
      />
    );
  };

  const getAdvanceSection = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    if (props.onTable) {
      return getVCheckboxField(filter, onChange, disabled);
    }

    return <AdvanceSection filter={filter} disabled={disabled} onChange={props.onChange} advance={props.advance} />;
  };

  const getGroupButtons = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    if (props.onTable) {
      return getVSelectField(filter, onChange, disabled);
    }

    return <GroupButtons filter={filter} disabled={disabled} onChange={onChange} />;
  };

  const getVCheckboxField = (filter: IFilterType, onChange: (value: any) => void, disabled?: boolean) => {
    return (
      <IndeterminateActionButton
        value={toJS(filter.value)}
        margin={props.tipLabel ? '7px 0px 0px' : '0'}
        tipLabel={props.tipLabel ? props.tipLabel : ''}
        disabled={disabled}
        onChange={(value: any) => {
          if (onChange) {
            onChange(value);
          }
        }}
        width={props.filter.width}
        excludeBoth={props.filter.excludeBoth}
        booleanLabels={props.filter.booleanLabels}
      />
    );
  };

  const getRadioField = (filter: IFilterType, onChange: (value: any) => void, disabled?: boolean) => {
    return (
      <RadioYesNo
        value={toJS(filter.value)}
        label={props.tipLabel ?? ''}
        disabled={disabled}
        onChange={(value: any) => {
          if (onChange) {
            onChange(value === 'YES');
          }
        }}
      />
    );
  };

  const getVNumberField = (filter: IFilterType, onChange: (value: any) => void, disabled?: boolean) => {
    return (
      <VNumericField
        ref={props.ref}
        tipLabel={props.tipLabel}
        buttonPosition={'none'}
        inline
        margin={'0'}
        noLabel={!props.showLabel}
        fill
        disabled={disabled}
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        value={toJS(filter.value)}
        label={props.showLabel ? filter.label : ''}
        id={'filter'}
        onChange={(value: any) => {
          if (onChange) {
            onChange(value);
          }
        }}
        minimal={props.minimal}
      />
    );
  };

  const getVPNumberField = (
    filter: IFilterType,
    onChange: (value: any) => void,
    disabled: boolean,
    props: any = {}
  ) => {
    return (
      <VNumericField
        ref={props.ref}
        tipLabel={props.tipLabel}
        buttonPosition={'none'}
        inline
        margin={'0'}
        noLabel={!props.showLabel}
        fill
        disabled={disabled}
        layer={{
          inputWidth: props.showLabel ? 8 : 12,
          labelOrientation: 'start',
          labelWidth: props.showLabel ? 4 : undefined
        }}
        value={toJS(filter.value)}
        label={props.showLabel ? filter.label : ''}
        id={'filter'}
        onChange={(value: any) => {
          if (onChange) {
            onChange(value);
          }
        }}
        minimal={props.minimal}
        {...{ props }}
      />
    );
  };

  const getVDateRangeField = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    let value = filter.value;
    const shortcuts = buildShortcuts(filter?.shortcuts);

    if (ProfileUtils.isDateRange(value)) {
      value = ProfileUtils.convertDateRange(value as DateRange);
    }

    const onOpeningPopover = (node: HTMLElement) => {
      const shortcuts = node.querySelectorAll('ul.bp3-menu li a');
      shortcuts.forEach((shortcut: Element) => {
        shortcut.classList.remove('bp3-active', 'bp3-intent-primary');
      });
    };

    const calculateMaxTime = (calculateMaxTime: string) => {
      const maxTimeString = ProfileUtils.evalStringToCode(calculateMaxTime);
      const maxTimeDate = moment(maxTimeString);
      return maxTimeDate.isValid() ? maxTimeDate.toDate() : moment('1/1/2100').toDate();
    };

    return (
      <div ref={refInputComponent}>
        {filter.type === 'DATERANGE' ? (
          <StyledDateRangeField
            isStacked={!!props.isStacked}
            id={`daterange-${filter.field}`}
            inline
            margin={'0'}
            layer={{
              inputWidth: props.showLabel ? 8 : 12,
              labelOrientation: 'start',
              labelWidth: props.showLabel ? 4 : undefined
            }}
            disabled={disabled}
            noLabel={!props.showLabel}
            fill
            format={'MM/DD/YYYY'}
            value={value}
            tipLabel={!props.onTable && !props.isQueryFilterInForm ? filter.label : undefined}
            label={props.showLabel ? filter.label : ''}
            allowSingleDayRange
            onChange={(value: any) => {
              if (onChange) {
                if (value) {
                  onChange(value);
                }
              }
            }}
            useAmPm={filter?.useAmPm}
            precision={filter?.timePrecision}
            shortcuts={shortcuts}
            maxTime={filter.calculateMaxTime ? calculateMaxTime(filter.calculateMaxTime) : undefined}
            popoverProps={{
              modifiers: { preventOverflow: { boundariesElement: 'viewport' } },
              onOpening: onOpeningPopover
            }}
            refWidth={widthComponent ?? 0}
            canClearSelection={filter.canClearSelection}
          />
        ) : (
          <CustomDateRange
            isStacked={props.isStacked}
            onTable={props.onTable}
            showLabel={props.showLabel}
            filter={filter}
            onChange={props.onChange}
            disabled={disabled}
            calculateMaxTime={calculateMaxTime}
            shortcuts={shortcuts}
            isQueryFilterInForm={props.isQueryFilterInForm}
            widthComponent={widthComponent}
          />
        )}
      </div>
    );
  };

  const getVColorField = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    const color = filter.value ? filter.value : '#fafafa';
    return (
      <>
        {props.showLabel && <label>{filter.label}:</label>}
        <VColorPicker
          Color={color}
          typePickerColor={'ChromePicker'}
          position={'top'}
          disable={disabled}
          width={30}
          height={30}
          onChange={(value: any) => {
            if (onChange) {
              onChange(value.hex);
            }
          }}
        />
      </>
    );
  };

  const getCustomTimeSelect = (filter: IFilterType, onChange: (value: any) => void, disabled: boolean) => {
    const isSpecific = filter?.isSpecific ?? true;

    const onChangeInternal = (value: any) => {
      props?.onChange({
        ...filter,
        value: value ? '00:00' : '',
        operator: value ? 'DATE_EQUAL' : 'EQUALITY',
        isSpecific: value
      });
    };

    const toggleUpDown = () => {
      onChangeInternal(!isSpecific);
    };

    return (
      <CustomTimeSelectBody onTable={props.onTable}>
        {!props.onTable && <SwitchCustomTimeSelect {...{ filter, disabled, isSpecific, onChange: onChangeInternal }} />}
        {isSpecific ? (
          <DateTimeFilter
            {...{
              filter,
              onChange,
              disabled: disabled,
              type: 'TIME',
              showLabel: props?.showLabel,
              disableContext: disableContext,
              tipLabel: props?.tipLabel,
              minimal: props?.minimal
            }}
          />
        ) : (
          getVSelectField(filter, onChange, disabled)
        )}
        {props.onTable && (
          <ToggleUpDown
            onDown={toggleUpDown}
            onUp={toggleUpDown}
            iconSize={12}
            disabledAll={disabled}
            disabledUp={!isSpecific}
            disabledDown={isSpecific}
            disabledColor={'#B0BEC5'}
          />
        )}
      </CustomTimeSelectBody>
    );
  };

  return <>{filter && getFields()}</>;
});

export default FilterField;
